import { useState, useEffect, useMemo, useCallback, memo, ReactElement } from 'react';
import Image from 'next/legacy/image';
import AliceCarousel from 'react-alice-carousel';
import 'react-alice-carousel/lib/alice-carousel.css';
import CloseIcon from '@mui/icons-material/Close';

import styles from './productwidget.module.css';
import ProgressBarInterval from '../ProgressBarInterval';
import { tryCatchHandlerSync } from '@/utils/commonUtilsTS';
import { WidgetItem } from '@/types/aboutUs';
import { STORAGE_KEYS } from '@/constants/const';

interface ProductWidgetProps {
    widgets?: Record<string, any>[];
}

const ProductWidget = memo<ProductWidgetProps>(({ widgets = [] }) => {
    const [isShow, setIsShow] = useState<boolean>(true);

    const getInitialMaxWidth = () => (typeof window !== 'undefined' && window.innerWidth < 768 ? 280 : 314);
    const [maxWidth, setMaxWidth] = useState<number>(getInitialMaxWidth);

    useEffect(() => {
        const handleResize = () => {
            setMaxWidth(window.innerWidth < 768 ? 280 : 314);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const getProductWidget: boolean =
            tryCatchHandlerSync(() =>
                JSON.parse(globalThis?.localStorage?.getItem(STORAGE_KEYS.CLOSE_PRODUCT_WIDGET) || '')
            ).data || false;
        setIsShow(getProductWidget ? false : true);
    }, []);

    const sections: WidgetItem[] = useMemo(() => {
        if (!widgets[0]) return [];
        const raw = widgets[0];
        return ['first', 'second', 'third']
            .map((prefix) => {
                const title = raw[`${prefix}HighlightSectionTitle`]?.value;
                const desc = raw[`${prefix}HighlightSectionDescription`]?.value;
                const media = raw[`${prefix}HighlightSectionMedia`]?.src;
                if (title && desc && media) {
                    return {
                        title,
                        description: desc,
                        media,
                        mediaWidth: raw[`${prefix}HighlightSectionMedia`]?.width,
                        mediaHeight: raw[`${prefix}HighlightSectionMedia`]?.height,
                        link: raw[`${prefix}HighlightSectionLink`]?.jsonValue?.value?.href || ''
                    } as WidgetItem;
                }
                return null;
            })
            .filter((x): x is WidgetItem => x !== null);
    }, [widgets]);

    const handleCloseWidget = useCallback(() => {
        setIsShow(false);
        localStorage.setItem(STORAGE_KEYS.CLOSE_PRODUCT_WIDGET, 'true');
    }, []);

    const slides: ReactElement[] = useMemo(
        () =>
            sections.map((item, idx) => (
                <div key={idx}>
                    <div className='ml-auto w-[24px] h-[24px] mr-4' onClick={handleCloseWidget}>
                        <CloseIcon sx={{ color: '#234455' }} />
                    </div>
                    <div className='text-[24px] leading-[36px] font-bold text-darkGreen mt-[-25px] max-w-[302px] md:max-w-[322px] m-auto'>
                        {item.title}
                    </div>
                    <div className='max-h-[338px]'>
                        <div
                            className='text-[16px] leading-[24px] text-[#666666] mt-6 max-w-[302px] md:max-w-[322px] m-auto'
                            dangerouslySetInnerHTML={{ __html: item?.description || '' }}
                        />
                        <div
                            className={`mt-4 ${styles.widgetImg}`}
                            onClick={() => item?.link && window.open(item?.link, '_self')}>
                            <Image
                                src={item?.media ?? ''}
                                alt=''
                                width={item?.mediaWidth && item?.mediaWidth < maxWidth ? item?.mediaWidth : maxWidth}
                                height={item?.mediaHeight && item?.mediaHeight < 300 ? item?.mediaHeight : 300}
                            />
                        </div>
                    </div>
                </div>
            )),
        [sections, maxWidth, handleCloseWidget]
    );

    const renderPrevBtn = useCallback(
        () => (
            <div className={`${styles.btn} ${styles.prev}`}>
                <Image src='/assets/images/ProductListing/arrow-left.svg' width={8} height={12} alt='Prev' />
            </div>
        ),
        []
    );
    const renderNextBtn = useCallback(
        () => (
            <div className={`${styles.btn} ${styles.next}`}>
                <Image src='/assets/images/ProductListing/arrow-right.svg' width={8} height={12} alt='Next' />
            </div>
        ),
        []
    );
    const renderDotsItem = useCallback(
        ({ isActive }: { isActive: boolean }) => (
            <div className='relative mr-[5px]'>
                {isActive ? (
                    <div className='mt-[9px]'>
                        <ProgressBarInterval />
                    </div>
                ) : (
                    <Image src='/assets/images/ProductListing/dot.svg' width={7} height={6} alt='Dot' />
                )}
            </div>
        ),
        []
    );

    if (!isShow || slides.length === 0) return null;

    return (
        <div
            className={`${styles.mobileWidget} alice-carousel-widget__dots w-[350px] md:w-[394px] h-[460px] bg-[#FFF1E2] fixed md:bottom-[16px] md:right-[172px] z-30 md:z-[1] rounded-lg py-4 text-center`}>
            <AliceCarousel
                mouseTracking
                items={slides}
                infinite
                animationType='slide'
                autoPlay={slides.length > 1}
                autoPlayInterval={5000}
                autoPlayDirection='ltr'
                autoPlayStrategy='all'
                renderPrevButton={renderPrevBtn}
                renderNextButton={renderNextBtn}
                renderDotsItem={renderDotsItem}
                responsive={{ 0: { items: 1 }, 1024: { items: 1 } }}
                disableButtonsControls={slides.length === 1}
                disableDotsControls={slides.length === 1}
            />
        </div>
    );
});

ProductWidget.displayName = 'ProductWidget';
export default ProductWidget;
